import {
  getValueFromObject,
  isExistAndPositiveInteger,
  accusative
} from './common';
import { makeHostSum, normalizePath } from './url';
import { getItemSEOQuery, getSEOData } from '../../api/queries';
import CONFIG from '../../config';
import { getI18nForLang } from './getI18nForLang';

const rangeTranslation = (from, to, unit, currI18n) => {
  let result;
  if (isExistAndPositiveInteger(from) && isExistAndPositiveInteger(to)) {
    result = currI18n.t('search.search_title_service.from_and_to', {
      from,
      to,
      unit
    });
  } else if (isExistAndPositiveInteger(from)) {
    result = currI18n.t('search.search_title_service.from', {
      from,
      unit
    });
  } else if (isExistAndPositiveInteger(to)) {
    result = currI18n.t('search.search_title_service.to', {
      to,
      unit
    });
  }

  return result;
};

export const getClearAsPath = asPath =>
  asPath.split('?')[0].split('#')[0] || asPath;

export const getBasedSeoData = (asPath, req, withoutQueryParams = false) => {
  const newAsPath = normalizePath(asPath);
  const rawHostSum = req ? makeHostSum({ asPath: newAsPath, req }) : null;
  const hostSum = withoutQueryParams
    ? {
        ...rawHostSum,
        currentUrl: getClearAsPath(rawHostSum.currentUrl)
      }
    : rawHostSum;

  return { newAsPath, hostSum };
};

export const getSeoData = async (
  apolloClient,
  asPath = '/',
  req,
  withoutQueryParams = false
) => {
  if (!apolloClient) {
    return {};
  }

  const { newAsPath, hostSum } = getBasedSeoData(
    asPath,
    req,
    withoutQueryParams
  );

  const {
    data: { seo }
  } = await apolloClient.query({
    query: getSEOData,
    variables: { path: newAsPath }
  });

  return {
    title: seo?.title ?? null,
    description: seo?.description ?? null,
    h1: seo?.h1 ?? null,
    asPath: newAsPath,
    hostSum
  };
};

export const getCustomSeoData = (customData, asPath = '/', req) => {
  const { newAsPath, hostSum } = getBasedSeoData(asPath, req);

  return {
    ...customData,
    asPath: newAsPath,
    hostSum
  };
};

export const getResidenceSeoData = (name, lng, asPath, req) => {
  const threadSafeI18n = getI18nForLang(lng || CONFIG.defaultLocale);

  const title = name
    ? threadSafeI18n.t('pages.residence.meta_title', {
        lng,
        residence_name: name
      })
    : null;

  const description = name
    ? threadSafeI18n.t('pages.residence.meta_description', {
        lng,
        residence_name: name
      })
    : null;

  return getCustomSeoData(
    {
      title,
      description,
      h1: name ?? null
    },
    asPath,
    req
  );
};

const generateSeoMetaTags = metaTags =>
  metaTags?.reduce(
    (result, { name, content }) => ({ ...result, [name]: content }),
    {}
  ) ?? {};

export const generateItemSeoObj = (item, newAsPath, hostSum) => {
  const seo = generateSeoMetaTags(item?.metaTags);

  return {
    title: seo?.title ? `${seo?.title} - bina.az` : null,
    description: seo?.description ?? null,
    asPath: newAsPath,
    hostSum
  };
};

export const getItemSeoData = async (apolloClient, asPath, req) => {
  if (!apolloClient) {
    return {};
  }
  const id = asPath.match(/\d+/)?.[0];
  const newAsPath = normalizePath(asPath);
  const hostSum = makeHostSum({ asPath: newAsPath, req });

  const {
    data: { item }
  } = await apolloClient.query({ query: getItemSEOQuery, variables: { id } });

  return generateItemSeoObj(item, newAsPath, hostSum);
};

export const createSeoConfig = (seoData, fbAppId, currI18n) => {
  if (!seoData || Object.keys(seoData).length === 0) {
    return false;
  }

  const title = seoData.title || currI18n.t('default_title');
  const description = seoData.description
    ? seoData.description
    : currI18n.t('default_description');
  const { currentUrl, path } = seoData?.hostSum;

  return {
    title,
    description,
    openGraph: {
      url: path === '/' ? currentUrl : null
    },
    facebook: {
      appId: fbAppId
    }
  };
};

export const getInitialSeoData = (router, lng, seoData, fbAppId, currI18n) => {
  const path = router.state?.asPath || '/';
  const host = lng === 'ru' ? `${lng}.${CONFIG.host}` : CONFIG.host;

  const defaultHostSum = {
    currentUrl: `${CONFIG.protocol}://${host}`,
    protocol: CONFIG.protocol,
    host: CONFIG.host,
    path
  };

  const seo = seoData?.hostSum?.path
    ? seoData
    : { ...seoData, hostSum: defaultHostSum };

  return { seo, seoConfig: createSeoConfig(seo, fbAppId, currI18n) };
};

export class SearchItemsTitleService {
  constructor(data, filter, currI18n) {
    this.data = data;
    this.filter = filter;
    this.i18n = currI18n;
  }

  action = () => {
    if (typeof this.filter.leased === 'undefined') return null;
    const type = this.filter.leased ? 'rent' : 'buy';

    return this.i18n.t(`search.search_title_service.${type}`);
  };

  category = () => {
    if (!isExistAndPositiveInteger(this.filter.categoryId))
      return this.i18n.t('search.search_title_service.empty_category');
    return accusative(
      getValueFromObject(
        this.data.categories,
        this.filter.categoryId,
        'name'
      ).toLowerCase(),
      this.i18n.language
    );
  };

  rooms = () => {
    if (!this.filter.roomIds || this.filter.roomIds.length > 1) {
      return '';
    }
    return this.i18n.t('search.form.room_count', {
      postProcess: 'interval',
      count: this.filter.roomIds[0] !== '5+' ? this.filter.roomIds[0] : '5'
    });
  };

  city = () => {
    return getValueFromObject(this.data.cities, this.filter.cityId, 'name');
  };

  area = () => {
    const units = this.i18n.t('search.search_title_service.units.metres');

    return rangeTranslation(
      this.filter.areaFrom,
      this.filter.areaTo,
      units,
      this.i18n
    );
  };

  price = () => {
    return rangeTranslation(
      this.filter.priceFrom,
      this.filter.priceTo,
      'AZN',
      this.i18n
    );
  };

  location = () => {
    const locationsNamesArray =
      this.filter.locationIds &&
      this.filter.locationIds.map(item => {
        return getValueFromObject(this.data.locations, item, 'name');
      });

    if (!locationsNamesArray || locationsNamesArray.length > 1) {
      return null;
    }

    return locationsNamesArray[0];
  };

  getTitle = () => {
    const title = this.i18n
      .t('search.search_title_service.format', {
        action: this.action(),
        category: this.category(),
        rooms: this.rooms(),
        city: this.city(),
        area: this.area(),
        price: this.price(),
        location: this.location()
      })
      .split()
      .join(' ')
      .replace(/\s,/g, ',')
      .replace(/,+,*/g, ',')
      .replace(/,\s$/, '')
      .replace(/\s, /, '')
      .trim();
    return `${title.replace(/^./, title[0].toUpperCase())}`;
  };
}

export class SearchItemsH1Service extends SearchItemsTitleService {
  price = () => {
    return (
      (!(this.filter.locationIds && this.filter.locationIds.length > 1) &&
        rangeTranslation(
          this.filter.priceFrom,
          this.filter.priceTo,
          'AZN',
          this.i18n
        )) ||
      ''
    );
  };

  location = () => {
    const locationsNamesArray =
      this.filter.locationIds &&
      this.filter.locationIds.map(item => {
        return getValueFromObject(this.data.locations, item, 'name', 'id');
      });

    return locationsNamesArray ? locationsNamesArray.join(', ') : null;
  };

  getH1 = () =>
    this.i18n
      .t('search.search_title_service.format', {
        action: this.action(),
        category: this.category(),
        rooms: this.rooms(),
        city: this.city(),
        area: this.area(),
        price: this.price(),
        location: this.location()
      })
      .split()
      .join(' ')
      .replace(/\s,/g, ',')
      .replace(/,+,*/g, ',')
      .replace(/,\s$/, '')
      .replace(/\s, /, '');
}

export const formatSearchResultSeoData = (
  seoData,
  currentLang,
  data,
  filter
) => {
  if (seoData.title && seoData.description) return seoData;
  // Use thread-safe i18n instance with specific language instead of singleton
  // This prevents race conditions when multiple requests run concurrently
  // Each request gets its own i18n instance with the correct language
  const threadSafeI18n = getI18nForLang(currentLang);

  const rawTitle = new SearchItemsTitleService(
    data,
    filter,
    threadSafeI18n
  ).getTitle();

  const title = `${rawTitle} - bina.az`;
  const description = threadSafeI18n.t('default_search_description', {
    title: rawTitle
  });

  if (!seoData.h1) {
    const h1 = new SearchItemsH1Service(data, filter, threadSafeI18n)
      .getH1()
      .trim();
    return { ...seoData, title, description, h1 };
  }

  return { ...seoData, title, description };
};
